import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import StepProgress from '../components/StepProgress';
import SelectionCard from '../components/SelectionCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Home, Building2, ChevronLeft, ChevronRight, Plus, Trash2, Shield, Zap, Info, AlertTriangle, CheckCircle2, ExternalLink, Upload, Download } from 'lucide-react';
import { estimateAnnualConsumptionPrivate, estimateAnnualConsumptionBusiness, deriveLoadProfile, estimatePeakLoad, buildGeneralRecommendation } from '../lib/general-sizing';
import { BATTERY_MODULE_BRUTTO_KWH, getDisplayName } from '../config/general-product-config';
import { industryOptions, shiftTypes } from '../config/load-profile-config';
import { injectorTypes, injectorStatusOptions, pvComplexityOptions, generatorPhaseOptions } from '../config/defaults';
import { exportGeneralPdf } from '../lib/pdf-export';
import WhatIfPanel from '../components/WhatIfPanel';
import axios from 'axios';
import FragHansFloating from './FragHansFloating';
import { API_BASE } from '../lib/api-base';

const API = `${API_BASE}/api`;
const STEPS = [{ label: 'Kundentyp' }, { label: 'Verbrauch' }, { label: 'Szenario' }, { label: 'Einspeiser' }, { label: 'Ergebnis' }];

const HANS_GREETINGS = [
  'Ich sehe, du entscheidest gerade zwischen Privat und Gewerbe. Wenn du unsicher bist, beschreib mir kurz dein Vorhaben — ich helfe dir bei der Einordnung.',
  'Du bist beim Verbrauch. Wenn du ihn nicht genau kennst, sag mir einfach Haushaltsgröße / Branche — ich schätze ihn für dich.',
  'Du wählst dein Einsatzszenario. Unsicher zwischen Netzparallel und Notstrom? Frag mich nach dem Unterschied.',
  'Jetzt geht es um Einspeiser wie PV, BHKW oder Wind. Beschreib sie mir in Worten — ich übersetze in die Eingabe.',
  'Hier siehst du deine Empfehlung. Soll ich sie dir erklären oder Alternativen zeigen?',
];

const initialData = {
  customerType: null, consumptionKnown: null, annualConsumption: '', consumptionEstimated: false,
  persons: 3, hasEv: false, hasHeatPump: false,
  industry: '', shiftType: 'single', weekendOperation: false, additionalBusinessLoadKw: 0,
  scenarioType: null, backupNeedClass: 'none', peakLoadKnown: null, peakLoadKw: '',
  injectors: [], injectorsExist: null, pvComplexityClass: 'standard',
  uploadedProfile: null, uploadFileName: null,
};

export default function GeneralConfigurator() {
  const [step, setStep] = useState(0);
  const [d, setD] = useState(initialData);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [profileClass, setProfileClass] = useState(null);  // aus FragHans-Navigation
  const navigate = useNavigate();
  const location = useLocation();
  const up = useCallback((key, val) => setD(prev => ({ ...prev, [key]: val })), []);
  const progressStep = step === 4 ? 5 : step;

  // Restore state when returning from LeadForm or prefill from Frag HANS
  useEffect(() => {
    const rs = location.state;
    if (rs?.restore && rs.data) {
      setStep(4);
      setD(prev => ({ ...prev, ...rs.data }));
      if (rs.data.result) setResult(rs.data.result);
      window.history.replaceState({}, '');
    } else if (rs?.prefill && rs.data) {
      // ProfileClass aus FragHans für die WhatIfPanel-Voreinstellung merken
      if (rs.profileClass) setProfileClass(rs.profileClass);
      // Bei skipToResult füllen wir fehlende Pflichtfelder mit sinnvollen
      // Defaults und springen direkt auf das Ergebnis, damit der Kunde mit
      // den WhatIf-Slidern spielen kann.
      const merged = {
        ...rs.data,
        _profileClass: rs.profileClass || null,
        // Frag-HANS-Empfehlung mitführen, damit die Slider-Modus-Ergebnisseite
        // dasselbe Modell als Primärempfehlung zeigt wie HANS verbal empfohlen
        // hat (verhindert Desync zwischen Chat-Karte und Result-Header).
        _recommendedModel: rs.recommendedModel || null,
      };
      if (rs.skipToResult) {
        if (!merged.customerType) merged.customerType = 'private';
        // Jahresverbrauch nur als "unbekannt" markieren, wenn TATSÄCHLICH
        // keiner geliefert wurde. Sonst verwirft der Konfigurator den vom
        // Backend gelieferten Wert und nutzt eine Personen-Schätzung.
        if (merged.consumptionKnown == null && !merged.annualConsumption) {
          merged.consumptionKnown = false;
          if (!merged.persons) merged.persons = 4;
        }
        if (!merged.scenarioType) merged.scenarioType = 'grid_parallel';
        if (!merged.backupNeedClass) merged.backupNeedClass = 'none';
      }
      setD(prev => ({ ...prev, ...merged }));
      if (rs.skipToResult) {
        // Berechnung direkt ausführen und zum Ergebnis springen.
        // Die Berechnung braucht die frischen Daten, also synchron mit
        // dem gemergten Objekt arbeiten statt aus State zu lesen.
        setTimeout(() => {
          computeResultWith(merged);
          setStep(4);
        }, 0);
      } else {
        setStep(merged.customerType ? 1 : 0);
        if (rs.autoCalc) {
          // Auto-advance through remaining steps to hit the calc button
          setTimeout(() => {
            const btn = document.querySelector('[data-testid="btn-next"]');
            if (btn && !btn.disabled) btn.click();
          }, 250);
        }
      }
      window.history.replaceState({}, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canNext = () => {
    if (step === 0) return !!d.customerType;
    if (step === 1) {
      if (d.customerType === 'business' && !d.industry) return false;
      if (d.consumptionKnown === true) return d.annualConsumption > 0;
      if (d.consumptionKnown === false) return d.customerType === 'private' ? d.persons > 0 : !!d.industry;
      return false;
    }
    if (step === 2) {
      if (!d.scenarioType) return false;
      if (d.scenarioType === 'backup_only') return !!d.backupNeedClass && d.backupNeedClass !== 'none';
      return true; // grid_parallel always ok once scenarioType is set
    }
    if (step === 3) return true;
    return false;
  };

  const goNext = () => { if (step === 3) computeResult(); setStep(s => Math.min(s + 1, 4)); };

  const handleLoadProfileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/load-profile`, formData);
      if (res.data.success) {
        up('uploadedProfile', res.data);
        up('uploadFileName', file.name);
        up('annualConsumption', res.data.annualConsumptionKwh);
        up('consumptionKnown', true);
      }
    } catch { /* fallback to standard */ }
    finally { setUploading(false); }
  };

  const computeResultWith = (dd) => {
    let consumption = Number(dd.annualConsumption);
    let estimated = false;
    if (dd.consumptionKnown === false || !consumption) {
      if (dd.customerType === 'private') consumption = estimateAnnualConsumptionPrivate({ persons: dd.persons, hasEv: dd.hasEv, hasHeatPump: dd.hasHeatPump });
      else consumption = estimateAnnualConsumptionBusiness({ industry: dd.industry || 'other', shiftType: dd.shiftType, weekendOperation: dd.weekendOperation, additionalLoadKw: dd.additionalBusinessLoadKw });
      estimated = true;
    }
    const loadProfile = deriveLoadProfile({ customerType: dd.customerType, industry: dd.industry, shiftType: dd.shiftType, weekendOperation: dd.weekendOperation, hasEv: dd.hasEv, hasHeatPump: dd.hasHeatPump });
    const peakLoadKw = dd.uploadedProfile?.peakLoadKw || estimatePeakLoad({ annualConsumption: consumption, loadProfile, hasHeatPump: dd.hasHeatPump, hasEv: dd.hasEv, additionalLoadKw: dd.additionalBusinessLoadKw });
    const rec = buildGeneralRecommendation({
      annualConsumption: consumption, customerType: dd.customerType, scenarioType: dd.scenarioType,
      backupNeedClass: dd.backupNeedClass, injectors: dd.injectors,
      pvComplexityClass: dd.pvComplexityClass, hasEv: dd.hasEv, hasHeatPump: dd.hasHeatPump,
      loadProfile, peakLoadKw, knownPeakLoadKw: dd.peakLoadKnown === true ? Number(dd.peakLoadKw) : null,
      shiftType: dd.shiftType, uploadedProfile: dd.uploadedProfile, consumptionEstimated: estimated,
      // §20 Villa/MFH-Re-Ranking: persons + profileClass werden gebraucht,
      // damit Floor-Regel im Konfigurator-Wizard greift (5+ Personen → Villa).
      persons: dd.persons,
      profileClass: dd._profileClass || profileClass,
      // Frag-HANS-Override: wenn Frag-HANS in der Chat-Karte ein konkretes
      // Modell empfohlen hat, MUSS die Slider-Modus-Ergebnisseite dasselbe
      // Modell als Primärempfehlung zeigen — sonst entsteht ein Desync
      // zwischen Chat ("HS 6 PV") und Result-Header ("Hans HS 3").
      recommendedModelOverride: dd._recommendedModel || null,
    });
    setResult({ ...rec, consumption, consumptionEstimated: estimated, loadProfile });
  };

  const computeResult = () => computeResultWith(d);

  const addInjector = (status = 'existing') => up('injectors', [...d.injectors, { id: Date.now(), type: 'pv', status, powerKw: '', annualYield: '', pvComplexity: 'standard' }]);
  const removeInjector = (id) => up('injectors', d.injectors.filter(inj => inj.id !== id));
  const updateInjector = (id, key, val) => up('injectors', d.injectors.map(inj => inj.id === id ? { ...inj, [key]: val } : inj));
  // Atomarer Multi-Field-Patch: verhindert Race-Conditions, wenn ein
  // onChange-Handler MEHRERE Felder gleichzeitig setzen will (User-Bug
  // 15.07.26 im Einspeiser-Typ-Dropdown: zweifaches `updateInjector`
  // basierte beide Aufrufe auf der stale-Closure → zweiter überschrieb
  // den ersten → Typwechsel PV→BHKW/Wind/Fuel-Cell hing).
  const patchInjector = (id, patch) => up('injectors', d.injectors.map(inj => inj.id === id ? { ...inj, ...patch } : inj));
  const goToLeadForm = () => navigate('/anfrage', { state: { module: 'general', summary: { ...d, result } } });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <StepProgress steps={STEPS} current={progressStep} />
      <main className="flex-1 pb-28 sm:pb-12">
        <div className="ees-container max-w-2xl">
          <div key={step} className="ees-step-enter">

            {/* STEP 0: Customer Type */}
            {step === 0 && (
              <div data-testid="step-customer-type">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">Wie nutzen Sie das System?</h2>
                <p className="text-sm text-gray-500 mb-6">Wählen Sie Ihren Anwendungsbereich.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectionCard icon={<Home className="w-7 h-7" />} title="Privat" description="Einfamilienhaus, Mehrfamilienhaus, privates Wohngebäude" selected={d.customerType === 'private'} onClick={() => up('customerType', 'private')} testId="card-private" />
                  <SelectionCard icon={<Building2 className="w-7 h-7" />} title="Gewerbe" description="Unternehmen, Landwirtschaft, kommunale Einrichtungen" selected={d.customerType === 'business'} onClick={() => up('customerType', 'business')} testId="card-business" />
                </div>
              </div>
            )}

            {/* STEP 1: Consumption */}
            {step === 1 && (
              <div data-testid="step-consumption">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">Jahresstromverbrauch</h2>
                <p className="text-sm text-gray-500 mb-6">{d.customerType === 'business' ? 'Angaben zu Ihrem Betrieb und Stromverbrauch.' : 'Kennen Sie Ihren jährlichen Stromverbrauch?'}</p>

                {d.customerType === 'business' && (
                  <div className="space-y-4 mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div><Label className="text-gray-600 text-sm">Branche</Label><select value={d.industry} onChange={e => up('industry', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-[#32373c] focus:ring-1 focus:ring-[#9ec043] outline-none" data-testid="select-industry"><option value="">Bitte wählen</option>{industryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                    <div><Label className="text-gray-600 text-sm">Arbeitszeiten</Label><select value={d.shiftType} onChange={e => up('shiftType', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-[#32373c] focus:ring-1 focus:ring-[#9ec043] outline-none" data-testid="select-shift">{shiftTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                    <div className="flex items-center gap-2"><Checkbox checked={d.weekendOperation} onCheckedChange={v => up('weekendOperation', !!v)} data-testid="check-weekend" id="weekend" /><Label htmlFor="weekend" className="text-gray-600 text-sm cursor-pointer">Wochenendbetrieb</Label></div>
                  </div>
                )}

                {d.consumptionKnown === null && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <SelectionCard title="Verbrauch bekannt" description="Ich kenne meinen Jahresstromverbrauch" onClick={() => up('consumptionKnown', true)} testId="card-consumption-known" />
                      <SelectionCard title="Verbrauch unbekannt" description="Ich möchte den Verbrauch schätzen lassen" onClick={() => up('consumptionKnown', false)} testId="card-consumption-unknown" />
                    </div>
                    {d.customerType === 'business' && (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
                        <Upload className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500 mb-2">Optional: Lastprofil hochladen (CSV/Excel)</p>
                        <label className="inline-flex items-center gap-1.5 text-xs text-[#9ec043] cursor-pointer font-medium hover:underline">
                          <input type="file" accept=".csv,.xlsx,.xls" onChange={handleLoadProfileUpload} className="hidden" data-testid="upload-loadprofile" />
                          {uploading ? 'Wird verarbeitet...' : 'Datei auswählen'}
                        </label>
                        {d.uploadFileName && <p className="text-xs text-gray-400 mt-1">Geladen: {d.uploadFileName}</p>}
                      </div>
                    )}
                  </div>
                )}

                {d.consumptionKnown === true && (
                  <div className="space-y-3">
                    <Label className="text-gray-600 text-sm">Jahresstromverbrauch in kWh</Label>
                    <Input type="number" placeholder="z. B. 5000" value={d.annualConsumption} onChange={e => up('annualConsumption', e.target.value)} className="bg-white text-[#32373c] border-gray-300" data-testid="input-consumption" />
                    <button className="text-xs text-[#9ec043] underline" onClick={() => up('consumptionKnown', null)} data-testid="btn-change-consumption-mode">Verbrauch stattdessen schätzen</button>
                  </div>
                )}

                {d.consumptionKnown === false && d.customerType === 'private' && (
                  <div className="space-y-4">
                    <div><Label className="text-gray-600 text-sm">Anzahl Personen im Haushalt</Label><div className="flex gap-2 mt-2">{[1,2,3,4,5].map(n => (<button key={n} onClick={() => up('persons', n)} className={`w-10 h-10 rounded-md border text-sm font-medium transition-colors ${d.persons === n ? 'border-[#9ec043] bg-[#f0f7e0] text-[#6b8a2a]' : 'border-gray-300 text-gray-500'}`} data-testid={`btn-persons-${n}`}>{n}{n===5?'+':''}</button>))}</div></div>
                    <div className="flex items-center gap-2"><Checkbox checked={d.hasEv} onCheckedChange={v => up('hasEv', !!v)} id="ev" data-testid="check-ev" /><Label htmlFor="ev" className="text-gray-600 text-sm cursor-pointer">E-Auto vorhanden oder geplant</Label></div>
                    <div className="flex items-center gap-2"><Checkbox checked={d.hasHeatPump} onCheckedChange={v => up('hasHeatPump', !!v)} id="hp" data-testid="check-heatpump" /><Label htmlFor="hp" className="text-gray-600 text-sm cursor-pointer">Wärmepumpe vorhanden oder geplant</Label></div>
                    <div className="text-xs text-gray-500 flex items-start gap-1.5 bg-[#f5f6f7] rounded p-3 border border-gray-100"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#9ec043]" /><span>Geschätzter Verbrauch: ca. <strong className="text-[#32373c]">{estimateAnnualConsumptionPrivate({ persons: d.persons, hasEv: d.hasEv, hasHeatPump: d.hasHeatPump }).toLocaleString('de-DE')} kWh/Jahr</strong>. Dies ist eine Näherung.</span></div>
                    <button className="text-xs text-[#9ec043] underline" onClick={() => up('consumptionKnown', null)}>Verbrauch stattdessen direkt eingeben</button>
                  </div>
                )}

                {d.consumptionKnown === false && d.customerType === 'business' && (
                  <div className="space-y-3">
                    <div><Label className="text-gray-600 text-sm">Große Verbraucher / Maschinen (Leistung in kW, optional)</Label><Input type="number" placeholder="z. B. 15" value={d.additionalBusinessLoadKw || ''} onChange={e => up('additionalBusinessLoadKw', Number(e.target.value))} className="mt-1 bg-white border-gray-300 w-32" data-testid="input-business-additional" /></div>
                    <div className="text-xs text-gray-500 flex items-start gap-1.5 bg-[#f5f6f7] rounded p-3 border border-gray-100"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#9ec043]" /><span>Wenn keine exakten Verbrauchs- oder Lastgangdaten vorliegen, arbeitet der Konfigurator mit vereinfachten Annahmen.</span></div>
                    <button className="text-xs text-[#9ec043] underline" onClick={() => up('consumptionKnown', null)}>Verbrauch stattdessen direkt eingeben</button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Scenario */}
            {step === 2 && (
              <div data-testid="step-scenario">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">Versorgungsszenario</h2>
                <p className="text-sm text-gray-500 mb-6">Wie soll das System eingesetzt werden?</p>
                {!d.scenarioType && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <SelectionCard icon={<Shield className="w-7 h-7" />} title="Notstrom / Backup" description="Reines Notstrom- oder Backupsystem" onClick={() => up('scenarioType', 'backup_only')} testId="card-backup" />
                    <SelectionCard icon={<Zap className="w-7 h-7" />} title="Netzparallelbetrieb" description="Energiespeicher im Netzparallelbetrieb mit optionaler Netzersatzfunktion" onClick={() => up('scenarioType', 'grid_parallel')} testId="card-grid-parallel" />
                  </div>
                )}
                {d.scenarioType === 'grid_parallel' && (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-[#9ec043]" /> Netzparallelbetrieb gewählt <button className="text-xs text-[#9ec043] underline ml-2" onClick={() => { up('scenarioType', null); up('backupNeedClass', 'none'); }}>Ändern</button></div>
                    <Label className="text-gray-600 text-sm">Soll das System auch Netzersatz können?</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[{v:'none',l:'Nein',d:'Kein Netzersatz benötigt'},{v:'critical_loads',l:'Eingeschränkter Netzersatzbetrieb 230 Volt',d:'Grundsätzliche Netzersatzversorgung'},{v:'full_backup',l:'Vollwertiger Netzersatzbetrieb 400 Volt Drehstrom',d:'Möglichst alles versorgen'}].map(o => (
                        <SelectionCard key={o.v} title={o.l} description={o.d} selected={d.backupNeedClass===o.v} onClick={() => up('backupNeedClass', o.v)} testId={`card-backup-${o.v}`} />
                      ))}
                    </div>
                    {d.backupNeedClass !== 'none' && (
                      <div className="space-y-3 pt-2">
                        <Label className="text-gray-600 text-sm">Kennen Sie Ihre Höchstlast?</Label>
                        <div className="flex gap-3"><SelectionCard title="Ja" selected={d.peakLoadKnown===true} onClick={() => up('peakLoadKnown', true)} testId="card-peak-known" className="flex-1" /><SelectionCard title="Nein" selected={d.peakLoadKnown===false} onClick={() => up('peakLoadKnown', false)} testId="card-peak-unknown" className="flex-1" /></div>
                        {d.peakLoadKnown === true && <div><Label className="text-gray-600 text-sm">Höchstlast in kW</Label><Input type="number" placeholder="z. B. 12" value={d.peakLoadKw} onChange={e => up('peakLoadKw', e.target.value)} className="mt-1 bg-white border-gray-300 w-32" data-testid="input-peak-load" /></div>}
                      </div>
                    )}
                  </div>
                )}
                {d.scenarioType === 'backup_only' && (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-[#9ec043]" /> Notstrom/Backup gewählt <button className="text-xs text-[#9ec043] underline ml-2" onClick={() => { up('scenarioType', null); up('backupNeedClass', 'none'); }}>Ändern</button></div>
                    <Label className="text-gray-600 text-sm">Welche Art von Netzersatz benötigen Sie?</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[{v:'critical_loads',l:'Eingeschränkter Netzersatzbetrieb 230 Volt',d:'Grundsätzliche Netzersatzversorgung'},{v:'full_backup',l:'Vollwertiger Netzersatzbetrieb 400 Volt Drehstrom',d:'Möglichst alles versorgen'}].map(o => (
                        <SelectionCard key={o.v} title={o.l} description={o.d} selected={d.backupNeedClass===o.v} onClick={() => up('backupNeedClass', o.v)} testId={`card-backup-${o.v}`} />
                      ))}
                    </div>
                    {d.backupNeedClass && d.backupNeedClass !== 'none' && (
                      <div className="space-y-3 pt-2">
                        <Label className="text-gray-600 text-sm">Kennen Sie Ihre Höchstlast?</Label>
                        <div className="flex gap-3"><SelectionCard title="Ja" selected={d.peakLoadKnown===true} onClick={() => up('peakLoadKnown', true)} testId="card-peak-known" className="flex-1" /><SelectionCard title="Nein" selected={d.peakLoadKnown===false} onClick={() => up('peakLoadKnown', false)} testId="card-peak-unknown" className="flex-1" /></div>
                        {d.peakLoadKnown === true && <div><Label className="text-gray-600 text-sm">Höchstlast in kW</Label><Input type="number" placeholder="z. B. 12" value={d.peakLoadKw} onChange={e => up('peakLoadKw', e.target.value)} className="mt-1 bg-white border-gray-300 w-32" data-testid="input-peak-load-backup" /></div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* STEP 3: Injectors */}
            {step === 3 && (
              <div data-testid="step-injectors">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">Einspeiser</h2>
                <p className="text-sm text-gray-500 mb-6">Gibt es bereits Einspeiser oder sind Einspeiser geplant?</p>
                {d.injectorsExist === null && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <SelectionCard title="Keine Einspeiser" onClick={() => up('injectorsExist', 'none')} testId="card-no-injectors" />
                    <SelectionCard title="Ja, vorhanden" onClick={() => { up('injectorsExist', 'yes'); if (!d.injectors.length) addInjector('existing'); }} testId="card-injectors-existing" />
                    <SelectionCard title="Ja, geplant" onClick={() => { up('injectorsExist', 'yes'); if (!d.injectors.length) addInjector('planned'); }} testId="card-injectors-planned" />
                  </div>
                )}
                {(d.injectorsExist === 'yes' || d.injectorsExist === 'none') && (
                  <div className="space-y-4">
                    {d.injectorsExist === 'none' && <div className="text-sm text-gray-500">Keine Einspeiser. <button className="text-xs text-[#9ec043] underline" onClick={() => up('injectorsExist', null)}>Ändern</button></div>}
                    {d.injectors.map((inj, idx) => (
                      <div key={inj.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                        <div className="flex items-center justify-between"><span className="text-sm font-medium text-[#32373c]">Einspeiser {idx+1}</span><button onClick={() => removeInjector(inj.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-gray-500 text-xs">Typ</Label><select value={inj.type} onChange={e => { const newType = e.target.value; patchInjector(inj.id, newType !== 'generator' ? { type: newType, generatorPhase: '' } : { type: newType }); }} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid={`select-injector-type-${idx}`}>{injectorTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                          <div><Label className="text-gray-500 text-xs">Status</Label><select value={inj.status} onChange={e => updateInjector(inj.id, 'status', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">{injectorStatusOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-gray-500 text-xs">Leistung ({inj.type==='pv'?'kWp':'kW'})</Label><Input type="number" placeholder="z. B. 10" value={inj.powerKw} onChange={e => updateInjector(inj.id, 'powerKw', Number(e.target.value))} className="mt-1 bg-white border-gray-300" data-testid={`input-injector-power-${idx}`} /></div>
                          {inj.type === 'generator' ? (
                            <div><Label className="text-gray-500 text-xs">Phasenzahl</Label><select value={inj.generatorPhase || ''} onChange={e => updateInjector(inj.id, 'generatorPhase', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid={`select-gen-phase-${idx}`}><option value="">Bitte wählen</option>{(d.customerType === 'business' ? generatorPhaseOptions.filter(o => o.value === 'three_phase') : generatorPhaseOptions).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                          ) : (
                            <div><Label className="text-gray-500 text-xs">Jahresertrag kWh (optional)</Label><Input type="number" placeholder="optional" value={inj.annualYield} onChange={e => updateInjector(inj.id, 'annualYield', Number(e.target.value))} className="mt-1 bg-white border-gray-300" /></div>
                          )}
                        </div>
                        {/* PV complexity only for PLANNED PV, not for existing */}
                        {inj.type === 'pv' && inj.status === 'planned' && (
                          <div><Label className="text-gray-500 text-xs">Art der PV-Anlage</Label><div className="flex gap-2 mt-1">{pvComplexityOptions.map(o => (<button key={o.value} onClick={() => { updateInjector(inj.id, 'pvComplexity', o.value); up('pvComplexityClass', o.value); }} className={`text-xs px-3 py-1.5 rounded border transition-colors ${inj.pvComplexity===o.value ? 'border-[#9ec043] bg-[#f0f7e0] text-[#6b8a2a]' : 'border-gray-300 text-gray-500'}`}>{o.label}</button>))}</div></div>
                        )}
                      </div>
                    ))}
                    {d.injectorsExist === 'yes' && d.injectors.length < 5 && (
                      <Button variant="outline" size="sm" onClick={addInjector} className="text-gray-500"><Plus className="w-4 h-4 mr-1" /> Weiteren Einspeiser hinzufügen</Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* STEP 4: Result */}
            {step === 4 && result && (
              <div data-testid="step-result" id="general-result-content">
                {result.isProjectPath ? (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-[#9ec043] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-[#32373c] mb-3">Individuelle Projektberatung empfohlen</h2>
                    <p className="text-sm text-gray-500 max-w-lg mx-auto mb-6">{result.projectNote}</p>
                    <Button onClick={goToLeadForm} className="bg-[#9ec043] hover:bg-[#8aad38] text-white px-8 py-3">Beratung anfragen</Button>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">Ihre Systemempfehlung</h2>
                    <p className="text-sm text-gray-500 mb-6">Basierend auf Ihren Angaben haben wir folgende Empfehlung für Sie erstellt.</p>

                    {result.gsRecommendationForPrivate && (
                      <div
                        className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
                        data-testid="gs-recommendation-banner"
                      >
                        <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900">
                          <div className="font-semibold mb-1">Hinweis zur Systemgröße</div>
                          <p className="leading-relaxed">{result.gsRecommendationForPrivate.message}</p>
                        </div>
                      </div>
                    )}

                    <div className="ees-result-card p-5 sm:p-6 mb-4">
                      <div className="ees-badge ees-badge-green mb-3">Empfohlenes System</div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#32373c] mb-0.5">{result.variantA.displayName || getDisplayName(result.variantA.model)}</h3>
                      <p className="text-sm text-gray-500 mb-4">Systemleistung: {result.variantA.maxDischargeKw} kW | Nutzbare Kapazität: {result.variantA.standardUsableKwh} kWh</p>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="rounded-lg bg-[#f5f6f7] p-3 border border-gray-100"><div className="text-xs text-gray-400 mb-1">Empfohlene Leistung</div><div className="text-lg font-semibold text-[#32373c]">ca. {result.targetPowerKw} kW</div></div>
                        <div className="rounded-lg bg-[#f5f6f7] p-3 border border-gray-100"><div className="text-xs text-gray-400 mb-1">Empfohlener Energieinhalt</div><div className="text-lg font-semibold text-[#32373c]">ca. {result.targetEnergyKwh} kWh</div></div>
                      </div>
                      {result.variantA.additionalBattery && (
                        <div className="text-xs text-amber-700 flex items-start gap-1.5 bg-amber-50 rounded p-3 mb-3 border border-amber-200"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Empfohlene zusätzliche Batteriekapazität: {result.variantA.additionalBattery.modules} x {BATTERY_MODULE_BRUTTO_KWH} kWh (brutto)</div>
                      )}
                      {result.genLink && <div className="ees-badge ees-badge-alt mb-2">+ Hans HS GenLink empfohlen</div>}
                    </div>

                    {result.variantB && (
                      <div className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6 mb-4">
                        <div className="ees-badge ees-badge-amber mb-3">Alternative</div>
                        <h3 className="text-lg font-semibold text-[#32373c] mb-0.5">{result.variantB.displayName || getDisplayName(result.variantB.model)}</h3>
                        <p className="text-xs text-gray-500 mb-1">Systemleistung: {result.variantB.maxDischargeKw} kW | Nutzbare Kapazität: {result.variantB.standardUsableKwh} kWh</p>
                        {result.variantB.additionalBattery && <p className="text-xs text-gray-400 mt-2">+ {result.variantB.additionalBattery.modules} x {BATTERY_MODULE_BRUTTO_KWH} kWh zusätzliche Batterie</p>}
                      </div>
                    )}

                    <div className="rounded-lg border border-gray-100 bg-[#f5f6f7] p-4 mb-4"><h4 className="text-sm font-semibold text-[#32373c] mb-2">Warum dieses System?</h4><p className="text-sm text-gray-600 leading-relaxed">{result.reasoning}</p></div>

                    <div className="mb-4"><h4 className="text-sm font-semibold text-[#32373c] mb-2">Vorteile auf einen Blick</h4><ul className="space-y-1.5">{result.advantages.map((a, i) => (<li key={i} className="flex items-start gap-2 text-sm text-gray-600"><CheckCircle2 className="w-4 h-4 text-[#9ec043] mt-0.5 shrink-0" /> {a}</li>))}</ul></div>

                    {result.warnings.length > 0 && <div className="mb-4 space-y-2">{result.warnings.map((w, i) => (<div key={i} className="text-xs text-amber-700 flex items-start gap-1.5 bg-amber-50 rounded p-3 border border-amber-200"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w}</div>))}</div>}

                    <Accordion type="single" collapsible className="mb-6">
                      <AccordionItem value="hints" className="border-gray-200"><AccordionTrigger className="text-sm text-gray-500 hover:no-underline">Hinweise und Annahmen</AccordionTrigger><AccordionContent className="text-xs text-gray-400 space-y-2">
                        <p>Unverbindliche Erstempfehlung. Die finale technische Auslegung erfolgt durch EES.</p>
                        {result.consumptionEstimated && <p>Bei fehlenden Verbrauchs-, Ertrags- oder Lastgangdaten arbeitet die Berechnung mit vereinfachten Annahmen.</p>}
                        {result.isCapped && <p>Die empfohlene Speicherkapazität wurde an der realistisch ladbaren Einspeisemenge ausgerichtet.</p>}
                        <p>Für Sonderkonfigurationen oder Projektanwendungen beraten wir Sie individuell.</p>
                      </AccordionContent></AccordionItem>
                    </Accordion>

                    {/* „Was ändert sich, wenn…"-Sandbox — Slider + Tagessimulation.
                        Erscheint NICHT im PDF-Export (jsPDF rendert nur explizit hinzugefügte Inhalte). */}
                    <WhatIfPanel
                      endpoint="/api/configurator/what-if"
                      showApproximationNote={true}
                      initialPvKwp={(() => {
                        const inj = (d.injectors || []).find(i => i?.type === 'pv');
                        return inj?.powerKw || 7;
                      })()}
                      initialBuildingType={(() => {
                        // Priorität: FragHans-profileClass → Konfigurator-Input
                        if (profileClass === 'private_villa') return 'villa';
                        if (profileClass === 'private_mfh') return 'multi_family';
                        if (d.customerType === 'business') return 'standard';
                        if ((d.persons || 0) >= 5) return 'villa';
                        return 'standard';
                      })()}
                      initialHasExistingPv={(() => {
                        const inj = (d.injectors || []).find(i => i?.type === 'pv');
                        return inj?.status === 'existing';
                      })()}
                      initialProfileClass={(() => {
                        // Wenn Hans eine Profilklasse mitgeschickt hat, diese immer nutzen.
                        if (profileClass) return profileClass;
                        // Mapping von Konfigurator-Eingaben auf SLP-Profilklassen
                        if (d.customerType === 'business') {
                          const ind = (d.industry || '').toLowerCase();
                          if (ind.includes('büro') || ind.includes('office')) return 'office';
                          if (ind.includes('handwerk') || ind.includes('werkstatt')) return 'workshop';
                          if (ind.includes('produktion') || ind.includes('fertigung')) return 'production';
                          if (ind.includes('handel') || ind.includes('retail')) return 'retail';
                          if (ind.includes('gastro') || ind.includes('hotel')) return 'gastro';
                          if (ind.includes('land') || ind.includes('agri')) return 'agri_general';
                          return 'office';
                        }
                        if ((d.persons || 0) >= 5) return 'private_villa';
                        return 'private_efh';
                      })()}
                    />

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button onClick={goToLeadForm} className="bg-[#9ec043] hover:bg-[#8aad38] text-white px-8 py-3 flex-1" data-testid="btn-request-consultation">Beratung anfragen</Button>
                      <Button variant="outline" onClick={() => exportGeneralPdf(result, d)} className="text-gray-500 border-gray-300" data-testid="btn-pdf-download"><Download className="w-4 h-4 mr-1" /> Als PDF speichern</Button>
                    </div>
                    <div className="mt-3">
                      <Button variant="outline" asChild className="text-gray-500 border-gray-300 w-full"><a href="https://www.ees-energiespeicher.de/" target="_blank" rel="noopener noreferrer">Zurück zur EES-Startseite <ExternalLink className="w-4 h-4 ml-1" /></a></Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {step < 4 && (
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-100">
              <Button variant="ghost" onClick={() => step===0 ? navigate('/') : setStep(s=>s-1)} className="text-gray-500"><ChevronLeft className="w-4 h-4 mr-1" /> {step===0?'Startseite':'Zurück'}</Button>
              <Button onClick={goNext} disabled={!canNext()} className="bg-[#9ec043] hover:bg-[#8aad38] text-white disabled:opacity-30" data-testid="btn-next">{step===3?'Empfehlung berechnen':'Weiter'} <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          )}
          {step === 4 && <div className="mt-6 pt-4 border-t border-gray-100"><Button variant="ghost" onClick={() => setStep(3)} className="text-gray-500"><ChevronLeft className="w-4 h-4 mr-1" /> Eingaben ändern</Button></div>}
        </div>
      </main>
      <FragHansFloating
        context={{
          configurator: 'general',
          step,
          stepLabel: STEPS[step]?.label || 'Unbekannt',
          data: {
            customerType: d.customerType,
            annualConsumption: d.annualConsumption || null,
            persons: d.persons,
            hasEv: d.hasEv,
            hasHeatPump: d.hasHeatPump,
            industry: d.industry || null,
            scenarioType: d.scenarioType,
            backupNeedClass: d.backupNeedClass,
            injectorsCount: d.injectors?.length || 0,
          },
        }}
        initialGreeting={HANS_GREETINGS[step] || HANS_GREETINGS[0]}
      />
    </div>
  );
}
